import { OHLCV, OrderSide } from "@cede/types";
import { DefaultError, InvalidOHLCVFormatError } from "../errors";
import { Logger, LoggerType } from "../logger";

/**
 * @description Merge the OHLCV data
 */
export const mergeCandleFields = (priceA: number, priceB: number, orderSideA: OrderSide, orderSideB: OrderSide) => {
  if (orderSideA === "buy" && orderSideB === "buy") return 1 / (priceA * priceB);
  else if (orderSideA === "sell" && orderSideB === "sell") return priceA * priceB;
  else if (orderSideA === "buy" && orderSideB === "sell") return priceB / priceA;
  // <=> (orderSideA === "sell" && orderSideB === "buy")
  else return priceA / priceB;
};

/**
 * @description Create a virtual candle from x real candles based on the sides of the orders.
 * @param candles If we want to get the virtual candle between token A and C using token B, we need to provide the candles of A/B and B/C.
 * @param sides The sides of the orders that created the candles.
 * @param exchangeId The exchange id.
 * @returns A virtual candle. If the candles params contains A/B and B/C the virtual candle returned will be A/C.
 */
export const createVirtualCandle = (candles: OHLCV[], sides: OrderSide[], exchangeId: string, logger: LoggerType) => {
  const candlesLength = candles.length;
  if (candlesLength === 0) {
    return null;
  }
  const timestamp = candles[0]?.time;
  if (!timestamp) {
    throw new InvalidOHLCVFormatError("Timestamp is missing").exchange(exchangeId);
  }
  const mergedCandle = candles[0] as OHLCV;

  // We always want to start from a sell order
  if (sides[0] === "buy") {
    mergedCandle.close = 1 / mergedCandle.close;
    mergedCandle.open = 1 / mergedCandle.open;
    mergedCandle.high = 1 / mergedCandle.high;
    mergedCandle.low = 1 / mergedCandle.low;
  }

  for (let i = 1; i < candlesLength; i++) {
    const candle = candles[i];
    if (!candle || candle.time !== timestamp) {
      // Should never happen
      logger.log(
        `Candles are not aligned | exchangeId=${exchangeId} | timestamp=${timestamp} | candle=${
          candle ? JSON.stringify(candle) : "null"
        }`,
        Logger.types.ERROR,
      );
      throw new InvalidOHLCVFormatError("Candles are not aligned #1").exchange(exchangeId);
    }
    // SideA is always "sell" because virtualCandles are calculated to do a sell order and if the first received candle is a buy order, we invert the prices
    const sideA = "sell";
    const sideB = sides[i];
    if (!sideB) {
      // Should never happen
      throw new DefaultError();
    }
    mergedCandle.open = mergeCandleFields(mergedCandle.open, candle.open, sideA, sideB);
    mergedCandle.high = mergeCandleFields(mergedCandle.high, candle.high, sideA, sideB);
    mergedCandle.low = mergeCandleFields(mergedCandle.low, candle.low, sideA, sideB);
    mergedCandle.close = mergeCandleFields(mergedCandle.close, candle.close, sideA, sideB);
    mergedCandle.volume += candle.volume;
  }
  mergedCandle.volume /= candlesLength;
  mergedCandle.time = timestamp;
  return mergedCandle;
};

/**
 * @description Align the candles by keeping only candles where all arrays have a candle at the same timestamp.
 * Exemple: We have candles for pair A/B and B/C, but they have different time ranges or gaps.
 * We keep only candles where both A/B and B/C have data at the exact same timestamp.
 * @param OHLCV The candles to align.
 * @returns The aligned candles (only timestamps present in ALL arrays).
 * @throws InvalidOHLCVFormatError If no common timestamps exist.
 */
export const alignCandles = (OHLCV: OHLCV[][], exchangeId: string) => {
  if (OHLCV.length === 0) {
    return [[]];
  }

  if (OHLCV.length === 1) {
    return OHLCV;
  }

  // Build a map of timestamp -> candle for each array for O(1) lookup
  const candleMaps = OHLCV.map((candles) => {
    const map = new Map<number, OHLCV>();
    for (const candle of candles) {
      map.set(candle.time, candle);
    }
    return map;
  });

  // Find timestamps that exist in ALL arrays
  const firstMap = candleMaps[0] as Map<number, OHLCV>;
  const commonTimestamps: number[] = [];

  for (const timestamp of firstMap.keys()) {
    const existsInAll = candleMaps.every((map) => map.has(timestamp));
    if (existsInAll) {
      commonTimestamps.push(timestamp);
    }
  }

  if (commonTimestamps.length === 0) {
    return [[]];
  }

  // Sort timestamps to maintain chronological order
  commonTimestamps.sort((a, b) => a - b);

  // Build aligned arrays with only common timestamps
  const alignedOHLCV: OHLCV[][] = candleMaps.map((map) => {
    return commonTimestamps.map((timestamp) => map.get(timestamp) as OHLCV);
  });

  return alignedOHLCV;
};
